import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  pageStyle,
  colors,
  typography,
  buttons,
  shadows,
  radius,
} from "../../theme";
import Spinner from "../common/Spinner";
import { formatPhoneRu } from "../../utils/formatPhoneRu";

const PHONE_PREFIX = "+7 (";
const MAX_PHONE_DIGITS = 10; // digits after 7

function formatDigits(digits) {
  let result = PHONE_PREFIX;

  if (!digits) {
    return result;
  }

  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 8);
  const part4 = digits.slice(8, 10);

  if (part1) {
    result += part1;
    if (part1.length === 3) {
      result += ") ";
    }
  }

  if (part2) {
    result += part2;
    if (part2.length === 3) {
      result += "-";
    }
  }

  if (part3) {
    result += part3;
    if (part3.length === 2) {
      result += "-";
    }
  }

  if (part4) {
    result += part4;
  }

  return result;
}

function StudentsList() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archivedCount, setArchivedCount] = useState(0);
  const navigate = useNavigate();

  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNameError, setNewNameError] = useState(false);

  const [newPhoneDigits, setNewPhoneDigits] = useState("");
  const [newPhoneError, setNewPhoneError] = useState(false);

  const [modalOffset, setModalOffset] = useState(0);

  const newNameInputRef = useRef(null);
  const newPhoneInputRef = useRef(null);

  useEffect(() => {
    setLoading(true);

    Promise.all([
      fetch("/api/students").then(r => r.json()),
      fetch("/api/students/archived").then(r => r.json()),
    ])
      .then(([active, archived]) => {
        setStudents(active || []);
        setArchivedCount((archived || []).length);
      })
      .finally(() => setLoading(false));
  }, []);

  const isTouchDevice = () => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches
    );
  };

  useEffect(() => {
    if (!showAddModal) return;
    if (typeof window === "undefined") return;
    if (!isTouchDevice()) return;
    if (!window.visualViewport) return;

    const vv = window.visualViewport;
    const baseHeight = vv.height;

    const handleResize = () => {
      const h = vv.height;
      if (h < baseHeight - 80) {
        setModalOffset(-160);
      } else {
        setModalOffset(0);
      }
    };

    vv.addEventListener("resize", handleResize);
    return () => {
      vv.removeEventListener("resize", handleResize);
    };
  }, [showAddModal]);

  const openAddModal = () => {
    setNewName("");
    setNewNameError(false);
    setNewPhoneDigits("");
    setNewPhoneError(false);
    setModalOffset(0);
    setShowAddModal(true);
    setTimeout(() => {
      if (newNameInputRef.current) {
        newNameInputRef.current.focus();
      }
    }, 0);
  };

  const ensureCaretAfterPrefix = () => {
    const input = newPhoneInputRef.current;
    if (!input) return;
    const prefixLen = PHONE_PREFIX.length;
    window.requestAnimationFrame(() => {
      let pos = input.selectionStart ?? 0;
      if (pos < prefixLen) {
        pos = prefixLen;
      }
      input.setSelectionRange(pos, pos);
    });
  };

  const handleNewPhoneFocus = () => {
    ensureCaretAfterPrefix();
  };

  const handleNewPhoneClick = () => {
    ensureCaretAfterPrefix();
  };

  const handleNewPhoneChange = e => {
    const raw = e.target.value || "";

    // Only digits
    let digits = raw.replace(/\D/g, "");

    // If the user is typing inside a string that visually starts with "+7",
    // drop that leading "7" so our internal state is only the 10 digits after 7
    if (raw.trim().startsWith("+7") && digits.startsWith("7")) {
      digits = digits.slice(1);
    }

    digits = digits.slice(0, MAX_PHONE_DIGITS);

    setNewPhoneDigits(digits);
    setNewPhoneError(false);
  };

  const handleNewPhonePaste = e => {
    e.preventDefault();
    const text = e.clipboardData.getData("text") || "";

    let digits = text.replace(/\D/g, "");

    // If pasted string contains "+7" at the start, drop that "7"
    if (text.trim().startsWith("+7") && digits.startsWith("7")) {
      digits = digits.slice(1);
    }

    digits = digits.slice(0, MAX_PHONE_DIGITS);

    setNewPhoneDigits(digits);
    setNewPhoneError(false);

    setTimeout(() => {
      ensureCaretAfterPrefix();
    }, 0);
  };

  const validateNewStudentFields = () => {
    let ok = true;

    if (!newName.trim()) {
      setNewNameError(true);
      ok = false;
      if (newNameInputRef.current) {
        newNameInputRef.current.focus();
      }
    } else {
      setNewNameError(false);
    }

    if (newPhoneDigits.length > 0 && newPhoneDigits.length < MAX_PHONE_DIGITS) {
      setNewPhoneError(true);
      ok = false;
      if (!newNameError && newPhoneInputRef.current) {
        newPhoneInputRef.current.focus();
        ensureCaretAfterPrefix();
      }
    } else if (newPhoneDigits.length === 0) {
      setNewPhoneError(false);
    } else if (newPhoneDigits.length === MAX_PHONE_DIGITS) {
      setNewPhoneError(false);
    }

    return ok;
  };

  const saveNewStudent = async () => {
    const valid = validateNewStudentFields();
    if (!valid) return;

    const payload = {
      name: newName.trim(),
      phone: newPhoneDigits.length ? newPhoneDigits : "",
    };

    try {
      const res = await fetch("/api/students", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        console.error("Error creating student");
        return;
      }

      const created = await res.json();
      setStudents(prev => [...prev, created]);
      setShowAddModal(false);
      setModalOffset(0);
      navigate(`/student/${created.id}`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleNewStudentSubmit = e => {
    e.preventDefault();
    saveNewStudent();
  };

  return (
    <div
      style={{
        background: colors.bg,
        minHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      <div style={pageStyle}>
        <header
          style={{
            marginBottom: 24,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <h1
            style={{
              fontSize: typography.pageTitle,
              margin: 0,
              color: colors.textMain,
            }}
          >
            Ученики
          </h1>
        </header>

        <button
          onClick={openAddModal}
          style={{
            ...buttons.primary,
            fontSize: 26,
            marginBottom: 28,
          }}
        >
          + Добавить ученика
        </button>

        {loading && (
          <div
            style={{
              padding: 20,
            }}
          >
            <Spinner size={36} />
          </div>
        )}

        {!loading && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: 20,
              }}
            >
              {students.map(s => (
                <div
                  key={s.id}
                  onClick={() => navigate(`/student/${s.id}`)}
                  style={{
                    padding: 24,
                    background: colors.card,
                    borderRadius: radius.card,
                    cursor: "pointer",
                    boxShadow: shadows.card,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      marginBottom: 6,
                      color: colors.textMain,
                      fontSize: 32,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {s.name}
                  </div>
                  {s.phone && (
                    <div
                      style={{
                        fontSize: typography.body,
                        marginTop: "auto",
                        color: colors.textSoft,
                      }}
                    >
                      {formatPhoneRu(s.phone)}
                    </div>
                  )}
                </div>
              ))}

              {students.length === 0 && (
                <div
                  style={{
                    fontSize: typography.bodyLarge,
                    color: colors.textSoft,
                    paddingTop: 8,
                  }}
                >
                  {archivedCount > 0
                    ? "Все студенты в архиве"
                    : "Пока учеников нет"}
                </div>
              )}
            </div>

            <div
              style={{
                marginTop: 28,
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <button
                onClick={() => navigate("/archive")}
                style={{
                  ...buttons.secondary,
                  fontSize: 24,
                  borderRadius: radius.pill,
                  background: "#fef3c7",
                  color: "#92400e",
                }}
              >
                Перейти в архив
              </button>
            </div>
          </>
        )}
      </div>

      {showAddModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 16,
            zIndex: 9999,
            overflowX: "hidden",
          }}
        >
          <div
            style={{
              background: colors.card,
              padding: 24,
              borderRadius: radius.card,
              width: "90%",
              maxWidth: 380,
              boxSizing: "border-box",
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.5)",
              fontSize: typography.body,
              color: colors.textMain,
              transform: `translateY(${modalOffset}px)`,
              transition: "transform 0.18s ease-out",
            }}
          >
            <form
              onSubmit={handleNewStudentSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 18,
              }}
            >
              <h2
                style={{
                  fontSize: typography.sectionTitle,
                  margin: 0,
                  color: colors.textMain,
                }}
              >
                Новый ученик
              </h2>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <label
                  style={{
                    fontSize: typography.body,
                    color: colors.textSoft,
                  }}
                >
                  Имя
                </label>
                <input
                  ref={newNameInputRef}
                  style={{
                    fontSize: typography.bodyLarge,
                    padding: 14,
                    borderRadius: radius.button,
                    border: `1px solid ${
                      newNameError ? "#fca5a5" : colors.borderSoft
                    }`,
                    background: newNameError ? "#fee2e2" : "white",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                  placeholder="Имя"
                  value={newName}
                  onChange={e => {
                    setNewName(e.target.value);
                    if (newNameError && e.target.value.trim()) {
                      setNewNameError(false);
                    }
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <label
                  style={{
                    fontSize: typography.body,
                    color: colors.textSoft,
                  }}
                >
                  Телефон (необязательно)
                </label>
                <input
                  ref={newPhoneInputRef}
                  style={{
                    fontSize: typography.bodyLarge,
                    padding: 14,
                    borderRadius: radius.button,
                    border: `1px solid ${
                      newPhoneError ? "#fca5a5" : colors.borderSoft
                    }`,
                    background: newPhoneError ? "#fee2e2" : "white",
                    width: "100%",
                    boxSizing: "border-box",
                    color:
                      newPhoneDigits.length === 0
                        ? "#9ca3af"
                        : colors.textMain,
                  }}
                  value={formatDigits(newPhoneDigits)}
                  onChange={handleNewPhoneChange}
                  onPaste={handleNewPhonePaste}
                  onFocus={handleNewPhoneFocus}
                  onClick={handleNewPhoneClick}
                  onKeyDown={e => {
                    if (e.key === "Backspace") {
                      e.preventDefault();
                      if (newPhoneDigits.length > 0) {
                        setNewPhoneDigits(prev => prev.slice(0, -1));
                        setNewPhoneError(false);
                        setTimeout(() => {
                          ensureCaretAfterPrefix();
                        }, 0);
                      }
                      return;
                    }

                    if (e.key === "Enter") {
                      e.preventDefault();
                      saveNewStudent();
                    }
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 12,
                  marginTop: 10,
                }}
              >
                <button
                  type="submit"
                  style={{
                    ...buttons.primary,
                    flex: 1,
                    fontSize: 24,
                    padding: "14px 0",
                    boxShadow: shadows.button,
                  }}
                >
                  Добавить
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setModalOffset(0);
                  }}
                  style={{
                    ...buttons.secondary,
                    flex: 1,
                    fontSize: 22,
                    padding: "14px 0",
                  }}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentsList;
